import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Palette, DeviceMobile, Code, Lightning } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const WebdesignHero = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const blobRef = useRef(null);
  const laptopRef = useRef(null);
  const iconsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the blob shape
      gsap.fromTo(blobRef.current, 
        { 
          scale: 0.8, 
          rotation: -5,
          borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%'
        },
        { 
          scale: 1, 
          rotation: 0,
          borderRadius: '30% 60% 70% 40% / 50% 60% 30% 60%',
          duration: 2,
          ease: "power2.out"
        }
      );

      // Animate the laptop
      gsap.fromTo(laptopRef.current,
        { 
          y: 50, 
          opacity: 0,
          rotationY: -15
        },
        { 
          y: 0, 
          opacity: 1,
          rotationY: 0,
          duration: 1.5,
          delay: 0.5,
          ease: "back.out(1.7)"
        }
      );

      // Animate floating icons
      gsap.to(iconsRef.current.children, {
        y: -20,
        duration: 2,
        ease: "power2.inOut",
        stagger: 0.2,
        yoyo: true,
        repeat: -1
      });

      // Continuous blob morphing
      gsap.to(blobRef.current, {
        borderRadius: '70% 30% 50% 50% / 30% 50% 70% 70%',
        duration: 4,
        ease: "power2.inOut",
        yoyo: true,
        repeat: -1
      });

    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={heroRef} className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
          </div>

      <div className="container mx-auto px-4 pt-20 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
          {/* Left Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h1 className="text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Professionele website{' '}
              <span className="text-blue-400">laten maken?</span>
            </h1>
            <p className="text-blue-400 text-xl font-medium mb-4">
              Wij ontwerpen websites die converteren
            </p>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed max-w-2xl">
              Van visitekaartje sites tot complexe business platforms. We maken websites die niet alleen mooi zijn, 
              maar ook daadwerkelijk klanten aantrekken en converteren.
            </p>
            
            {/* CTA Buttons */}
            <div className="space-y-4">
              <div>
                <Link to="/start-your-project" className="inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg group transition-all duration-300 transform hover:scale-105">
                  OFFERTE AANVRAGEN
                  <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" weight="thin" />
                </Link>
              </div>
              <div>
                <Link to="/contact" className="inline-flex items-center text-gray-300 hover:text-blue-400 transition-colors cursor-pointer group">
                  <span className="mr-2">Liever eerst sparren?</span>
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" weight="thin" />
                </Link>
              </div>
            </div>

            {/* Feature Icons */}
            <div ref={iconsRef} className="flex items-center space-x-8 mt-12">
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Palette className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Design</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <DeviceMobile className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Responsive</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Code className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Development</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Lightning className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Performance</span>
              </div>
            </div>
          </motion.div>

          {/* Right Visual Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative flex justify-center items-center hidden lg:flex"
          >
            {/* Animated Blob Container */}
            <div 
              ref={blobRef}
              className="relative w-[500px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-[30%_60%_70%_40%_/_50%_60%_30%_60%] flex items-center justify-center"
              style={{
                boxShadow: '0 0 100px rgba(59, 130, 246, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              {/* Laptop Mockup */}
              <div 
                ref={laptopRef}
                className="relative w-80 h-56 bg-gray-800 rounded-lg shadow-2xl transform perspective-1000"
                style={{
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                }}
              >
                {/* Laptop Screen */}
                <div className="absolute inset-2 bg-white rounded overflow-hidden">
                  {/* Mockup Website Content */}
                  <div className="h-full bg-gradient-to-br from-gray-50 to-gray-100 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-20 h-6 bg-emerald-500 rounded"></div>
                      <div className="flex space-x-2">
                        <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                        <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                        <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                      </div>
                    </div>
                    
                    {/* Hero Section Mockup */}
                    <div className="bg-white rounded-lg p-3 shadow-sm mb-3">
                      <div className="w-full h-12 bg-gradient-to-r from-emerald-400 to-teal-500 rounded mb-2"></div>
                      <div className="h-2 bg-gray-200 rounded mb-1"></div>
                      <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-emerald-500 rounded w-1/3"></div>
                    </div>
                    
                    {/* Content Sections */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white rounded p-2 shadow-sm">
                        <div className="w-full h-6 bg-gradient-to-r from-teal-400 to-cyan-500 rounded mb-1"></div>
                        <div className="h-1 bg-gray-200 rounded mb-1"></div>
                        <div className="h-1 bg-gray-200 rounded w-2/3"></div>
                      </div>
                      <div className="bg-white rounded p-2 shadow-sm">
                        <div className="w-full h-6 bg-gradient-to-r from-emerald-400 to-green-500 rounded mb-1"></div>
                        <div className="h-1 bg-gray-200 rounded mb-1"></div>
                        <div className="h-1 bg-gray-200 rounded w-2/3"></div>
                      </div>
                      <div className="bg-white rounded p-2 shadow-sm">
                        <div className="w-full h-6 bg-gradient-to-r from-cyan-400 to-blue-500 rounded mb-1"></div>
                        <div className="h-1 bg-gray-200 rounded mb-1"></div>
                        <div className="h-1 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Laptop Base */}
                <div className="absolute bottom-0 left-0 right-0 h-2 bg-gray-700 rounded-b-lg"></div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-emerald-500/30 rounded-full blur-sm"></div>
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-teal-500/30 rounded-full blur-sm"></div>
              <div className="absolute top-1/2 -right-8 w-12 h-12 bg-cyan-500/30 rounded-full blur-sm"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WebdesignHero; 